import {
  RadioButtonGroup,
  RadioButton,
  OverflowMenu,
  Tile,
  Button,
} from '@carbon/react';
import { useSetAtom, useAtomValue, useAtom } from 'jotai';
import { mapStylesIndexAtom } from './map';
import { standardMapStyles } from './map-styles';
import { MapLayerSelector } from 'components/map/map-layers-selector';

import style from './map.module.scss';
import { Currency, Layers, Close, Ruler, ZoomReset } from '@carbon/react/icons';
import { MapUserConfigAtom } from './types';
import { Fragment, ReactNode, useState, useEffect } from 'react';
import { useComponentVisible } from 'lib/hooks/useComponentVisible';
import {
  measureModeAtom,
  measurePointsAtom,
  measureFinishedAtom,
} from './atoms';
import { useOwnMapControls } from './control-slot';

export function StylesSelector({
  showMapFilters,
  mapConfigAtom,
  scopedLayers,
  wellsV2MinZoom,
  currentZoom,
  isLandingPageMap,
  onResetZoom,
  hideMeasureAndPricing = false,
}: {
  showMapFilters: boolean;
  mapConfigAtom: MapUserConfigAtom;
  scopedLayers: ReactNode;
  wellsV2MinZoom?: number;
  currentZoom?: number;
  isLandingPageMap: boolean;
  onResetZoom?: () => void;
  hideMeasureAndPricing?: boolean;
}) {
  const setMapStyleIndex = useSetAtom(mapStylesIndexAtom);
  // Contributed by features mounted inside this map; empty on a map with none.
  const mapControls = useOwnMapControls();
  const mapStyleIndex = useAtomValue(mapStylesIndexAtom);
  const [measureMode, setMeasureMode] = useAtom(measureModeAtom);
  const setMeasurePoints = useSetAtom(measurePointsAtom);
  const setMeasureFinished = useSetAtom(measureFinishedAtom);
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false, isLandingPageMap);
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);
  const [mapFilterContainerHeight, setMapFilterContainerHeight] =
    useState<number>(760);

  useEffect(() => {
    if (!isLandingPageMap) return;

    const calculateMaxHeight = () => {
      // Subtract 100px to account for header, margins, and other UI elements
      const windowHeight = window.innerHeight - 100;
      setMapFilterContainerHeight(windowHeight);
    };

    // Calculate initial height
    calculateMaxHeight();

    // Add resize listener
    window.addEventListener('resize', calculateMaxHeight);

    // Cleanup
    return () => window.removeEventListener('resize', calculateMaxHeight);
  }, [isLandingPageMap]);

  return (
    <div className={style.stylesSelector} ref={ref}>
      <Button
        id="map-filters-button"
        kind={isComponentVisible ? 'secondary' : 'ghost'}
        hasIconOnly
        iconDescription="Layers"
        className={style.mapFilterBtn}
        size="sm"
        tooltipAlignment="center"
        tooltipPosition="left"
        onClick={() => setIsComponentVisible((current) => !current)}
        renderIcon={Layers}
      />
      {isComponentVisible && (
        <Tile
          className={
            isLandingPageMap
              ? style.controlsContainerLandingPage
              : style.controlsContainer
          }
          data-testid="controls-container"
          style={
            isLandingPageMap
              ? { maxHeight: `${mapFilterContainerHeight}px` }
              : undefined
          }
        >
          {showMapFilters && (
            <div className={style.layerSelection}>
              {scopedLayers && (
                <section>
                  <h3 className={style.label}>Scoped</h3>
                  {scopedLayers}
                </section>
              )}
              <section>
                {isLandingPageMap ? (
                  <div className={style.labelContainer}>
                    <h3 className={style.label}>General</h3>
                    <Button
                      hasIconOnly
                      kind="ghost"
                      renderIcon={Close}
                      iconDescription="Close"
                      size="sm"
                      onClick={() => setIsComponentVisible(false)}
                      className={style.closeButton}
                    />
                  </div>
                ) : (
                  <h3 className={style.label}>General</h3>
                )}
                <MapLayerSelector
                  mapConfigAtom={mapConfigAtom}
                  wellsV2MinZoom={wellsV2MinZoom}
                  currentZoom={currentZoom}
                />
              </section>
            </div>
          )}
          <section>
            <h3 className={style.label}>Visualization</h3>

            <div className={style.radioChecks}>
              <RadioButtonGroup
                name={`radio-button-${mapStyleIndex}`}
                valueSelected={standardMapStyles[mapStyleIndex].name}
              >
                {standardMapStyles.map((style, i) => (
                  <RadioButton
                    key={style.name}
                    labelText={style.name}
                    value={style.name}
                    onClick={() => setMapStyleIndex(i)}
                  />
                ))}
              </RadioButtonGroup>
            </div>
          </section>
        </Tile>
      )}
      {!hideMeasureAndPricing && (
        <>
          <OverflowMenu
            className={
              mapConfig.developmentAreas.options.pricing
                ? style.pricingActive
                : undefined
            }
            size="sm"
            flipped
            renderIcon={Currency}
            onClick={() =>
              setMapConfig((current) => ({
                ...current,
                developmentAreas: {
                  ...current.developmentAreas,
                  options: {
                    ...current.developmentAreas.options,
                    pricing: !current.developmentAreas.options.pricing,
                  },
                },
              }))
            }
          >
            <></>
          </OverflowMenu>
          <Button
            kind={measureMode ? 'secondary' : 'ghost'}
            hasIconOnly
            iconDescription="Measure distance"
            className={style.mapFilterBtn}
            size="sm"
            tooltipAlignment="center"
            tooltipPosition="left"
            onClick={() => {
              if (measureMode) {
                setMeasurePoints([]);
                setMeasureFinished(false);
              }
              setMeasureMode((current) => !current);
            }}
            renderIcon={Ruler}
          />
        </>
      )}
      {/* Whatever a feature mounted inside this map has contributed. The stack draws
          them without knowing what any of them is — see `control-slot`. */}
      {mapControls.map((control) => (
        <Fragment key={control.id}>
          {control.render({
            className: style.mapFilterBtn,
            // Contributed controls open to the left of this stack, as the layers
            // panel does, so one of the two has to give way.
            closePanels: () => setIsComponentVisible(false),
          })}
        </Fragment>
      ))}
      {onResetZoom && (
        <Button
          kind="ghost"
          hasIconOnly
          iconDescription="Reset zoom"
          className={style.mapFilterBtn}
          size="sm"
          tooltipAlignment="center"
          tooltipPosition="left"
          onClick={onResetZoom}
          renderIcon={ZoomReset}
        />
      )}
    </div>
  );
}
